import { cloneDeep } from 'lodash';
import React from 'react';
import { assign } from 'xstate';
import { Grid, Chip, Typography, Box } from '@mui/material';
import { IntlShape } from 'react-intl';

import { theme } from '../../theme/theme';
import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';
import { translateWithFallback } from '../../util';

import { getBackgroundColor, MultipleDisplayEnum } from './MultipleDisplayEnum';

/*
  ! Multiple Display basic component
*/
export interface MultipleDisplayComponent extends BaseComponent {
  type: 'multipleDisplay';
  props: MultipleDisplayProps;
}
export interface MultipleDisplayProps {
  message?: string;
  topics?: string[];
  bubbleType: MultipleDisplayEnum;
}

export const MultipleDisplay: React.FC<MultipleDisplayProps> = ({
  message,
  topics,
  bubbleType,
}) => {
  return (
    <>
      <Box
        sx={{
          borderRadius: '6px',
          backgroundColor: getBackgroundColor(bubbleType),
        }}
      >
        <Typography
          sx={{
            display: 'block',
            padding: '15px',
            fontFamily: theme.typography.fontFamily,
          }}
        >
          {message}
        </Typography>
      </Box>
      <Grid container direction="row" justifyContent="flex-end">
        {topics !== undefined &&
          topics.map((topic, index) => {
            return (
              <Chip
                key={index}
                label={topic}
                sx={{
                  transform: 'translate(-10px, -10px)',
                  borderRadius: '2px',
                  backgroundColor: '#e97229',
                  color: '#ffffff',
                  width: 'max-content',
                  marginLeft: '10px',
                }}
              />
            );
          })}
      </Grid>
    </>
  );
};

/*
  ! renderMultipleDisplayAction

  snippets from previos snippet input are rendered as a message mwith topics
*/
export const renderMultipleDisplayAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;
    const getValueFrom = payload.getValueFrom;
    let topics = payload.topics
      ? payload.topics.map((topic: string) => {
          return translateWithFallback(intl, topic);
        })
      : undefined;
    if (getValueFrom) {
      topics = context.userData[getValueFrom];
    }

    newContext.widgetData.push({
      type: 'multipleDisplay',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        message: translateWithFallback(intl, payload.message), // message from builder, translate here
        topics: topics,
        bubbleType: payload.bubbleType,
      },
    });
    return newContext;
  });
};

/*
  ! renderMultipleDisplaysAction

  snippets from previos snippet input are rendered as several messages
*/
export const renderMultipleDisplaysAction = assign(
  (context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );
    const payload = actionMetadata.action.payload;
    const getValueFrom = payload.getValueFrom;
    const messages = getValueFrom ? context.userData[getValueFrom] : undefined;

    if (messages && messages.length > 0) {
      messages.forEach((message: any) => {
        newContext.widgetData.push({
          type: 'multipleDisplay',
          temporary: actionMetadata.action.temporary ?? false,
          props: {
            message: message, // message from a variable - no translation here!
            topics: undefined,
            bubbleType: payload.bubbleType,
          },
        });
      });
    }

    return newContext;
  }
);
